import { useMemo } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { FileIcon } from "@l2r-front/l2r-icons"

import { I18N_NAMESPACE } from "../../constants/i18n"

import { L2RButton } from "../L2RButton"
import { ZoomableImage } from "../ZoomableImage"

import * as Styled from "./FileViewer.styled.js"

const unsupportedImageTypes = [
    "image/tiff",
    "image/tiff-fx",
]

export const FileViewer = (props: FileViewerProps) => {

    const {
        className,
        file,
        onFileChange,
        allowZoom = false,
        variant = "normal",
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)

    const fileInfo = useMemo(() => {
        if (!file) {
            return null
        }
        if (file instanceof File) {
            return {
                name: file.name,
                extension: file.name.split(".").length >= 2 ? file.name.split(".").pop() : "",
                type: file.type,
                url: URL.createObjectURL(file),
                isPDF: file.type === "application/pdf",
                isImage: file.type.startsWith("image/") && !unsupportedImageTypes.includes(file.type),
            }
        } else {
            return {
                name: file.filename,
                extension: file.filename.split(".").length >= 2 ? file.filename.split(".").pop() : "",
                type: file.mime_type,
                url: file.file,
                isPDF: file.mime_type === "application/pdf",
                isImage: file.mime_type.startsWith("image/") && !unsupportedImageTypes.includes(file.mime_type),
            }
        }
    }, [file])

    return (
        <Styled.Container className={className}>
            <Styled.ImageWrapper>
                {fileInfo?.isPDF && <iframe style={{ width: "100%", height: "100%" }} src={fileInfo?.url} title={fileInfo?.name} />}
                {fileInfo?.isImage && <ZoomableImage src={fileInfo?.url} filename={fileInfo?.name} disabled={!allowZoom} />}
                {fileInfo && !fileInfo.isPDF && !fileInfo.isImage && (
                    <Styled.FileIconWrapper >
                        <FileIcon />
                        {variant === "large" && <Styled.NoPreviewMessage variant="Mobile/Body/Bold">{t(I18N_NAMESPACE, "components.fileViewer.noPreview")}</Styled.NoPreviewMessage>}
                    </Styled.FileIconWrapper>
                )}
            </Styled.ImageWrapper>
            {file && onFileChange && <L2RButton
                onClick={onFileChange}
                variant="secondary">
                <Styled.ChangeIcon />
                {t(I18N_NAMESPACE, "components.fileViewer.change")}
            </L2RButton>}
        </Styled.Container>
    )
}

type FileViewerProps = {
    className?: string,
    file: File | {
        filename: string,
        mime_type: string,
        file: string,
    },
    onFileChange?: () => void,
    allowZoom?: boolean,
    variant?: "normal" | "large",
}
